import React, { useState, useEffect, useRef } from 'react';
import { Plus, ExternalLink } from 'lucide-react';
import { useSearchParams, Link } from 'react-router-dom';
import { SubmissionForm } from '../components/SubmissionForm';
import { VideoList } from '../components/VideoList';
import { FilterMenu } from '../components/FilterMenu';
import { PostedVideoCheckModal } from '../components/PostedVideoCheckModal';
import { useStore } from '../store';
import { generateThumbnailFromUrl, updateSubmissionThumbnail } from '../utils/video/generateThumbnailFromUrl';

export const Home: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [showForm, setShowForm] = useState(false);
  const [showPostedCheck, setShowPostedCheck] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const isProcessing = useRef(false);
  const processedSubmissions = useRef<Set<string>>(new Set());
  const postedCheckTriggered = useRef(false);
  
  // Load filters from URL params on mount
  const [selectedType, setSelectedType] = useState(() => {
    return searchParams.get('type') || '';
  });
  const [selectedStatus, setSelectedStatus] = useState(() => {
    return searchParams.get('status') || '';
  });
  const {
    fetchSubmissions,
    fetchArtists,
    submissions,
    submissionsPagination,
    selectedArtistId,
    setSelectedArtistId,
    artists
  } = useStore();

  const currentPage = submissionsPagination?.currentPage || parseInt(searchParams.get('page') || '1');

  // On mount, restore artist filter from URL if present
  useEffect(() => {
    const artistFromUrl = searchParams.get('artist');
    if (artistFromUrl && artistFromUrl !== selectedArtistId) {
      setSelectedArtistId(artistFromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update URL params when filters change (not during auto-generation)
  useEffect(() => {
    if (isGenerating) return;
    const params = new URLSearchParams(searchParams);
    if (selectedType) params.set('type', selectedType);
    else params.delete('type');
    if (selectedStatus) params.set('status', selectedStatus);
    else params.delete('status');
    if (selectedArtistId) params.set('artist', selectedArtistId);
    else params.delete('artist');
    setSearchParams(params);
  }, [selectedType, selectedStatus, selectedArtistId, isGenerating]);

  // Update URL params when page changes (not during auto-generation)
  useEffect(() => {
    if (isGenerating) return;
    const params = new URLSearchParams(searchParams);
    if (currentPage > 1) params.set('page', currentPage.toString());
    else params.delete('page');
    setSearchParams(params);
  }, [currentPage, isGenerating]);

  // Auto-generate thumbnails for database videos on current page when page changes
  useEffect(() => {
    const autoGenerateThumbnails = async () => {
      // Prevent re-triggering while already processing
      if (isProcessing.current) return;
      isProcessing.current = true;
      setIsGenerating(true);
      
      try {
        // Only check submissions currently loaded on this page
        const currentPageSubmissions = submissions.filter((s: any) => 
          !s.thumbnailUrl && s.videoUrl && !s.videoUrl.includes('dropbox.com')
        );
        
        if (currentPageSubmissions.length === 0) {
          isProcessing.current = false;
          setIsGenerating(false);
          return;
        }
        
        console.log(`Found ${currentPageSubmissions.length} submissions on page ${currentPage} without thumbnails (excluding Dropbox)`);
        
        // Filter to only Supabase storage URLs with supported formats
        const supportedVideos = currentPageSubmissions.filter((s: any) => {
          const url = s.videoUrl;
          const isOurStorage = url.includes('wrlgoxbzlngdtomjhvnz.supabase.co/storage/v1/object/public/videos');
          if (!isOurStorage) return false;
          const isSupportedFormat = url.match(/\.(mp4|webm|mov)(\?|$)/i);
          // Skip if already processed
          if (processedSubmissions.current.has(s.id.toString())) return false;
          return isSupportedFormat;
        });
        
        console.log(`Filtered to ${supportedVideos.length} supported database videos on page ${currentPage}`);
        
        if (supportedVideos.length === 0) {
          isProcessing.current = false;
          setIsGenerating(false);
          return;
        }
        
        console.log(`Auto-generating thumbnails for ${supportedVideos.length} submissions on page ${currentPage}`);
        
        let processed = 0;
        
        for (const submission of supportedVideos) {
          try {
            const thumbnailUrl = await generateThumbnailFromUrl(submission.videoUrl);
            
            if (thumbnailUrl) {
              await updateSubmissionThumbnail(submission.id.toString(), thumbnailUrl);
              processed++;
              processedSubmissions.current.add(submission.id.toString());
              console.log(`Auto-generated thumbnail for submission ${submission.id}`);
              
              // Small delay before next one
              await new Promise(resolve => setTimeout(resolve, 500));
            }
          } catch (error) {
            console.error(`Error generating thumbnail for submission ${submission.id}:`, error);
          }
        }
        
        if (processed > 0) {
          console.log(`Auto-generated ${processed} thumbnails on page ${currentPage}`);
          // Refresh once at the end
          await fetchSubmissions(currentPage, 20, {
            artistId: selectedArtistId || undefined,
            type: selectedType,
            status: selectedStatus
          }, true);
        }
      } catch (error) {
        console.error('Error auto-generating thumbnails:', error);
      } finally {
        isProcessing.current = false;
        setIsGenerating(false);
      }
    };
    
    autoGenerateThumbnails();
  }, [currentPage]);

  // Fetch data when component mounts or when shared artist filter changes
  useEffect(() => {
    const loadData = async () => {
      try {
        await Promise.all([
          fetchSubmissions(1, 20, {
            artistId: selectedArtistId || undefined,
            type: selectedType,
            status: selectedStatus
          }),
          fetchArtists()
        ]);
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };

    loadData();
  }, [fetchSubmissions, fetchArtists, selectedArtistId, selectedType, selectedStatus]);

  // Auto-show posted-video check modal if artist has IG URL and correction-needed/feedback-needed videos
  const currentArtist = artists.find((a: any) => a.id === selectedArtistId);
  const hasPendingVideos = submissions.some((s: any) => s.status === 'correction-needed' || s.status === 'feedback-needed');

  useEffect(() => {
    if (postedCheckTriggered.current) return;
    if (!selectedArtistId) return;
    if (!currentArtist) return;
    const igUrl = (currentArtist as any).instagram_url;
    if (!igUrl) return;
    if (!hasPendingVideos) return;
    // Wait for submissions to be loaded
    if (submissions.length === 0) return;
    postedCheckTriggered.current = true;
    setShowPostedCheck(true);
  }, [selectedArtistId, currentArtist, hasPendingVideos, submissions.length]);

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-semibold">Video Submissions ({submissionsPagination.totalCount})</h2>
          <p className="text-sm text-gray-600 mt-1">
            Manage and review video feedback
          </p>
        </div>
        <div className="flex gap-2">
          {/* {hasSubmissionsWithoutThumbnails && (
            <button
              onClick={handleGenerateThumbnails}
              disabled={generatingThumbnails}
              className="btn bg-purple-600 hover:bg-purple-700 text-white"
              title="Generate thumbnails for all videos without thumbnails in database"
            >
              {generatingThumbnails ? (
                <Loader className="h-5 w-5 mr-2 animate-spin" />
              ) : (
                <Image className="h-5 w-5 mr-2" />
              )}
              Generate Thumbnails (All)
            </button>
          )} */}
          {selectedArtistId && (
            <Link
              to={`/artist/${selectedArtistId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn bg-gray-800 hover:bg-gray-900 text-white"
              title="Open this artist's view in a new tab"
            >
              <ExternalLink className="h-5 w-5 mr-2" />
              Open Artist View
            </Link>
          )}
          <button onClick={() => setShowForm(true)} className="btn">
            <Plus className="h-5 w-5 mr-2" />
            New Submission
          </button>
        </div>
      </div>

      <FilterMenu
        selectedArtist={selectedArtistId || ''}
        setSelectedArtist={setSelectedArtistId}
        selectedType={selectedType}
        setSelectedType={setSelectedType}
        selectedStatus={selectedStatus}
        setSelectedStatus={setSelectedStatus}
      />

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <VideoList
          artistId={selectedArtistId || undefined}
          filters={{
            artistId: selectedArtistId || undefined,
            type: selectedType,
            status: selectedStatus,
          }}
          isGenerating={isGenerating}
        />
      </div>

      {showForm && (
        <SubmissionForm
          onClose={() => setShowForm(false)}
          artistId={selectedArtistId || undefined}
        />
      )}

      {showPostedCheck && currentArtist && (
        <PostedVideoCheckModal
          artistId={String(currentArtist.id)}
          artistName={currentArtist.name}
          onClose={() => setShowPostedCheck(false)}
          onResolved={async () => {
            // Reload submissions after resolving (skip cache to get updated statuses)
            await fetchSubmissions(1, 20, {
              artistId: selectedArtistId || undefined,
              type: selectedType,
              status: selectedStatus,
            }, true);
          }}
        />
      )}
    </div>
  );
};